import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { CreditCard, AlertTriangle, Download, ExternalLink, RefreshCw } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import { ExpiredIdClient } from "../types";
import DataSourcePanel from "./DataSourcePanel";

interface ApiResponse {
    clients: ExpiredIdClient[];
    configured: boolean;
    message?: string;
}

const tableStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
            minHeight: "48px",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

const today = () => new Date().toISOString().split("T")[0];

const ExpiredIdReports: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [clients, setClients] = useState<ExpiredIdClient[]>([]);
    const [configured, setConfigured] = useState<boolean>(true);
    const [serverMessage, setServerMessage] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (accounts.length === 0) return;
        setLoading(true);
        try {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) { setLoading(false); return; }
            const res = await fetch(`${getApiBaseUrl()}/api/expired-ids`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: ApiResponse = await res.json();
            setClients(data.clients || []);
            setConfigured(!!data.configured);
            setServerMessage(data.message || null);
            setError(null);
        } catch (err: any) {
            setError(err.message || "Failed to load expired IDs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [instance, accounts]);

    const filtered = useMemo(() => {
        if (!search) return clients;
        const s = search.toLowerCase();
        return clients.filter(c =>
            c.name.toLowerCase().includes(s) ||
            c.clientName.toLowerCase().includes(s) ||
            (c.email || "").toLowerCase().includes(s) ||
            (c.idvIdDocType || "").toLowerCase().includes(s) ||
            (c.idvIdDocExpiryDate || "").toLowerCase().includes(s)
        );
    }, [clients, search]);

    const overdue90 = filtered.filter(c => {
        if (!c.idvIdDocExpiryDate) return false;
        return Date.now() - new Date(c.idvIdDocExpiryDate).getTime() > 90 * 86400000;
    }).length;

    const columns: TableColumn<ExpiredIdClient>[] = [
        {
            name: "Name",
            selector: row => row.name,
            sortable: true,
            cell: row => (
                <a
                    href={`https://web.amlhq.com/client/party/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--accent-blue)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                    {row.name}
                    <ExternalLink size={12} />
                </a>
            ),
        },
        { name: "Client", selector: row => row.clientName, sortable: true },
        { name: "Email", selector: row => row.email || "—", sortable: true },
        { name: "ID Document Type", selector: row => row.idvIdDocType || "—", sortable: true },
        { name: "Expiry Date", selector: row => row.idvIdDocExpiryDate || "—", sortable: true },
    ];

    const downloadCSV = () => {
        const headers = ["Name", "Client", "Email", "ID Document Type", "Expiry Date"];
        const rows = filtered.map(c => [c.name, c.clientName, c.email || "", c.idvIdDocType, c.idvIdDocExpiryDate]);
        const esc = (v: any) => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [headers.join(","), ...rows.map(r => r.map(esc).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `expired-ids-${today()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <CreditCard size={20} />
                    <h1>Expired IDs</h1>
                </div>
            </div>

            {!configured && (
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>{serverMessage || "AML HQ credentials are not configured on the server. The report is showing no data."}</span>
                </div>
            )}

            {error && (
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading data: {error}</span>
                </div>
            )}

            <div className="stats-grid stats-grid-2">
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: "#dc2626" }}><AlertTriangle size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{filtered.length}</div>
                        <div className="stat-label">Expired IDs</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: "#f59e0b" }}><AlertTriangle size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{overdue90}</div>
                        <div className="stat-label">Expired &gt; 90 Days</div>
                    </div>
                </div>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <button onClick={fetchData} className="download-btn" title="Refresh">
                    <RefreshCw size={16} /> Refresh
                </button>
                <button onClick={downloadCSV} className="download-btn" title="Download CSV">
                    <Download size={16} /> Export CSV
                </button>
            </div>

            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filtered}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    noDataComponent={<div className="no-data">No expired ID documents.</div>}
                    customStyles={tableStyles}
                />
            </div>

            <DataSourcePanel
                summary={
                    <>
                        AML party records whose identity-document expiry date has passed. Names link out to the
                        AML HQ client record at <code>web.amlhq.com</code>.
                    </>
                }
                badge={configured ? "PRODUCTION DATA" : "NOT CONFIGURED"}
                badgeColor={configured ? undefined : "#f59e0b"}
                sections={[
                    {
                        title: "Backend endpoint",
                        items: [
                            { name: "GET /api/expired-ids", description: "Server-side proxy to AML HQ. Returns empty array until credentials are configured." },
                        ],
                    },
                    {
                        title: "Required environment variables",
                        items: [
                            { name: "AMLHQ_USERNAME", description: "AML HQ login email." },
                            { name: "AMLHQ_PASSWORD", description: "AML HQ password (used to mint a fresh JWT)." },
                            { name: "AMLHQ_TOKEN", description: "Optional: bypass username/password by supplying a long-lived token." },
                        ],
                    },
                    {
                        title: "Database tables read",
                        items: [
                            { name: "aml_hq_accounts", description: "Caches the most recent AML HQ JWT and its expiry. Currently holds a stale 2024 token; will be refreshed once env credentials are set." },
                        ],
                    },
                ]}
            />
        </div>
    );
};

export default ExpiredIdReports;
